import { useState } from "react";
import axios from "axios";
import { Check, ArrowRight, MapPin, Share2, Link, MessageCircle, Mail } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAIN_SITE_URL = process.env.REACT_APP_MAIN_SITE_URL;

// HAEVN Logo Component (SVG)
const HaevnLogo = ({ className = "", color = "#252627" }) => (
  <svg 
    viewBox="0 0 1554.72 337.36" 
    className={className}
    fill={color}
    data-testid="haevn-logo"
  >
    <g>
      <g>
        <path d="M48.51,97.71h109.87v44.85h19.21v-44.85h108.14c17.11,0,31.03,13.92,31.03,31.03s-13.92,31.03-31.03,31.03h-13.12v19.21h13.12c27.71,0,50.24-22.54,50.24-50.24s-22.54-50.24-50.24-50.24H48.51c-16.15,0-29.3-13.14-29.3-29.29v-.69c0-16.15,13.14-29.3,29.3-29.3s29.29,13.14,29.29,29.3v16.24h19.21v-16.24C97.01,21.76,75.25,0,48.51,0S0,21.76,0,48.51v.69c0,26.75,21.76,48.51,48.51,48.51Z"/>
        <path d="M287.46,239.66h-109.88v-46.23h-19.21v46.23H50.24c-17.11,0-31.03-13.92-31.03-31.03s13.92-31.03,31.03-31.03h13.12v-19.21h-13.12c-27.71,0-50.24,22.54-50.24,50.24s22.54,50.24,50.24,50.24h237.22c16.15,0,29.3,13.14,29.3,29.3v.69c0,16.15-13.14,29.3-29.3,29.3s-29.29-13.14-29.29-29.3v-16.24h-19.21v16.24c0,26.75,21.76,48.51,48.5,48.51s48.51-21.76,48.51-48.51v-.69c0-26.75-21.76-48.51-48.51-48.51Z"/>
        <path d="M177.59,49.2c0-16.54,13.45-29.99,29.99-29.99s29.99,13.45,29.99,29.99v15.55h19.21v-15.55c0-27.13-22.07-49.2-49.2-49.2s-49.2,22.07-49.2,49.2v15.55h19.21v-15.55Z"/>
        <path d="M158.38,288.16c0,16.54-13.45,29.99-29.99,29.99s-29.99-13.45-29.99-29.99v-15.55h-19.21v15.55c0,27.13,22.07,49.2,49.2,49.2s49.2-22.07,49.2-49.2v-15.55h-19.21v15.55Z"/>
        <polygon points="256.78 227.46 256.78 111.3 237.57 111.3 237.57 158.38 98.4 158.39 98.4 111.29 79.19 111.29 79.19 227.45 98.4 227.45 98.4 177.6 237.57 177.59 237.57 227.46 256.78 227.46"/>
      </g>
      <g>
        <polygon points="597.4 151.71 477.49 151.71 477.49 53.9 445.95 53.9 445.95 283.47 477.49 283.47 477.49 182.45 597.4 182.45 597.4 283.47 629.1 283.47 629.1 53.9 597.4 53.9 597.4 151.71"/>
        <polygon points="1090.09 84.63 1078.12 53.9 922.51 53.9 922.51 283.47 1090.09 283.47 1090.09 252.57 954.04 252.57 954.04 179.25 1050.1 179.25 1050.1 148.67 954.04 148.67 954.04 84.47 1090.09 84.63"/>
        <polygon points="1221.82 239.28 1150.1 53.9 1117.44 53.9 1206.94 283.47 1236.55 283.47 1326.05 53.9 1293.23 53.9 1221.82 239.28"/>
        <polygon points="1523.34 53.9 1523.34 226.95 1390.3 53.9 1362.77 53.9 1362.77 283.47 1394.15 283.47 1394.15 110.41 1527.18 283.47 1554.72 283.47 1554.72 53.9 1523.34 53.9"/>
        <polygon points="761.87 53.9 665.18 283.47 699.12 283.47 775.96 98.72 852.33 283.47 886.43 283.47 789.73 53.9 761.87 53.9"/>
      </g>
    </g>
  </svg>
);

// Share Buttons
const ShareButtons = () => {
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/join`;
  const shareText = "HAEVN is building a better way to date. Join the waitlist.";

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const input = document.createElement("input");
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <div className="flex flex-wrap gap-3" data-testid="share-buttons">
      <button
        onClick={handleCopyLink}
        className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-200 text-sm font-subhead text-gray-700 hover:border-haevn-teal hover:text-haevn-navy transition-all duration-200"
        data-testid="share-copy-link"
      >
        <Link className="w-4 h-4" />
        {copied ? "Copied!" : "Copy Link"}
      </button>
      <button
        onClick={() => window.open(`sms:?&body=${encodeURIComponent(shareText + " " + shareUrl)}`, "_blank")}
        className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-200 text-sm font-subhead text-gray-700 hover:border-haevn-teal hover:text-haevn-navy transition-all duration-200"
        data-testid="share-text"
      >
        <MessageCircle className="w-4 h-4" />
        Text
      </button>
      <button
        onClick={() => window.open(`mailto:?subject=${encodeURIComponent("HAEVN — Join the Waitlist")}&body=${encodeURIComponent(shareText + "\n\nJoin here: " + shareUrl)}`, "_blank")}
        className="flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-gray-200 text-sm font-subhead text-gray-700 hover:border-haevn-teal hover:text-haevn-navy transition-all duration-200"
        data-testid="share-email"
      >
        <Mail className="w-4 h-4" />
        Email
      </button>
    </div>
  );
};

export default function GeneralWaitlistPage() {
  const [email, setEmail] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [phone, setPhone] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);

    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    if (!zipCode || zipCode.length !== 5 || !/^\d+$/.test(zipCode)) {
      setError("Please enter a valid 5-digit ZIP code.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/general-waitlist`, {
        email,
        zip_code: zipCode,
        phone: phone || null,
        sms_opt_in: smsOptIn,
      });
      setMessage(response.data.message);
      setEmail("");
      setZipCode("");
      setPhone("");
      setSmsOptIn(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="general-waitlist-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100 py-4 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <a href={MAIN_SITE_URL} target="_blank" rel="noopener noreferrer" data-testid="header-logo">
            <HaevnLogo className="h-7 w-auto" color="#1E2A4A" />
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-12 pb-8 px-6 md:px-12" data-testid="general-hero">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-haevn-teal" />
            <span className="text-sm font-subhead text-haevn-teal uppercase tracking-wider">
              Waitlist
            </span>
          </div>

          <h1 className="font-headline text-4xl md:text-5xl text-haevn-navy mb-3">
            Join the HAEVN Network
          </h1>

          <div className="bg-haevn-navy/5 border-l-4 border-haevn-navy px-5 py-4 mb-8">
            <p className="text-lg font-subhead text-haevn-navy">
              HAEVN is expanding city by city.
              <br />Tell us where you are and we'll let you know when we're headed your way.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left Column */}
            <div>
              <h2 className="font-headline text-2xl text-haevn-navy mb-4">
                Not in one of our cities yet?
              </h2>

              <div className="space-y-4 text-gray-600 font-body">
                <p>
                  We're growing fast. Your ZIP code helps us know where to go next. The more interest in your area, the sooner we can launch there.
                </p>
                <p>
                  Join the waitlist and be the first to know when HAEVN arrives near you.
                </p>
              </div>

              <div className="mt-8 p-5 bg-gray-50 border border-gray-100">
                <h3 className="font-subhead text-gray-900 mb-3">How it works</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-haevn-teal/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-subhead text-haevn-teal">1</span>
                    </div>
                    <p className="text-sm text-gray-600 font-body">You sign up with your email and ZIP code.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-haevn-teal/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-subhead text-haevn-teal">2</span>
                    </div>
                    <p className="text-sm text-gray-600 font-body">We track demand in your area.</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-haevn-teal/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-subhead text-haevn-teal">3</span>
                    </div>
                    <p className="text-sm text-gray-600 font-body">When your city is ready, you'll be the first to know.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="bg-white p-6 md:p-8 border-2 border-gray-100 shadow-sm" data-testid="general-waitlist-form">
              <h3 className="font-headline text-xl text-haevn-navy mb-2">
                Get on the list
              </h3>
              <p className="text-sm text-gray-500 font-body mb-6">
                We'll reach out when HAEVN is coming to your area.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="bg-white border-2 border-gray-200 px-5 py-4 text-lg font-body transition-all duration-300 rounded-none placeholder:text-gray-400
                    focus:border-haevn-gold focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="general-email-input"
                />

                <div>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/[^\d+\-() ]/g, "").slice(0, 16))}
                    placeholder="Mobile number (optional)"
                    className="bg-white border-2 border-gray-200 px-5 py-4 text-lg font-body transition-all duration-300 rounded-none placeholder:text-gray-400
                      focus:border-haevn-gold focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    data-testid="general-phone-input"
                  />
                  <p className="text-xs text-gray-400 font-body mt-1.5 ml-1">
                    Get a text when your city goes live and when you have matches
                  </p>
                </div>

                <Input
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                  placeholder="ZIP code"
                  className="bg-white border-2 border-gray-200 px-5 py-4 text-lg font-body transition-all duration-300 rounded-none placeholder:text-gray-400
                    focus:border-haevn-gold focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="general-zip-input"
                  maxLength={5}
                />

                <label className="flex items-start gap-3 cursor-pointer group" data-testid="general-sms-opt-in">
                  <input
                    type="checkbox"
                    checked={smsOptIn}
                    onChange={(e) => setSmsOptIn(e.target.checked)}
                    className="mt-0.5 w-4 h-4 border-2 border-gray-300 rounded-sm text-haevn-gold focus:ring-haevn-gold accent-[#E29E0C] cursor-pointer"
                  />
                  <span className="text-sm text-gray-600 font-body leading-snug">
                    Text me when my city goes live and when I have new matches
                  </span>
                </label>

                {error && (
                  <span className="text-sm text-red-500 block">{error}</span>
                )}

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-haevn-gold text-white px-6 py-4 font-subhead text-lg hover:bg-haevn-gold/90 transition-all duration-300 rounded-none h-auto group"
                  data-testid="general-submit-btn"
                >
                  <span className="flex items-center justify-center gap-2">
                    {isSubmitting ? "Joining..." : "Join the Waitlist"}
                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Button>
              </form>

              {message && (
                <div
                  className="mt-6 p-5 bg-haevn-teal/10 border-2 border-haevn-teal text-gray-900 flex items-start gap-3"
                  data-testid="general-success-message"
                >
                  <div className="w-6 h-6 rounded-full bg-haevn-teal flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-body">{message}</span>
                </div>
              )}

              <p className="text-xs text-gray-400 font-body mt-4">
                No spam. Just updates on your city.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Share */}
      <section className="py-10 px-6 md:px-12" data-testid="general-share-section">
        <div className="max-w-4xl mx-auto">
          <div className="border-2 border-haevn-teal/20 bg-haevn-teal/5 p-6 md:p-8">
            <div className="flex items-center gap-3 mb-2">
              <Share2 className="w-5 h-5 text-haevn-teal" />
              <h3 className="font-headline text-lg text-haevn-navy">
                Share with a friend
              </h3>
            </div>
            <p className="text-sm text-gray-500 font-body mb-6 ml-8">
              Know someone who'd be a good fit? Send them the link.
            </p>
            <div className="ml-8">
              <ShareButtons />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 border-t border-gray-100">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <a href={MAIN_SITE_URL} target="_blank" rel="noopener noreferrer">
            <HaevnLogo className="h-5 w-auto" color="#1E2A4A" />
          </a>
          <span className="text-sm text-gray-400 font-body">
            Building meaningful connections, one city at a time.
          </span>
        </div>
      </footer>
    </div>
  );
}
